import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Grid, IconButton, Tooltip, Divider } from '@mui/material';
import { translate } from '../../../utils/i18n';
import { 
  VolumeUp as SpeakerIcon,
  Engineering as CannonIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendIcon,
  Timer as TimerIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';
import axios from 'axios';
import { keyframes } from '@mui/system';

// 애니메이션 정의
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const AIBehaviorAnalysis = ({ language, birdActivityData = [] }) => {
  const [deterrentStats, setDeterrentStats] = useState({
    todayTotal: 0,
    todaySuccess: 0,
    cannonFired: 0,
    speakerUsed: 0,
    successRate: 0,
    avgResponseTime: 0
  });
  
  const [recentDeterrents, setRecentDeterrents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleRecordCount, setVisibleRecordCount] = useState(10); // 표시할 기록 개수 - 5에서 10으로 증가
  const containerRef = React.useRef(null);
  const recordsContainerRef = React.useRef(null);
  
  // 퇴치 현황 데이터 가져오기
  const fetchDeterrentData = async () => {
    setIsLoading(true);
    try {
      // 오늘의 퇴치 기록 가져오기
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get('/api/deterrents/filtered/', {
        params: {
          date_from: today,
          date_to: today,
          per_page: 100,
          sort_by: 'date_desc'
        }
      });
      
      if (response.data && response.data.status === 'success') {
        const deterrents = response.data.deterrents || [];
        
        // 통계 계산
        const totalDeterrents = deterrents.length;
        const successCount = deterrents.filter(d => d.is_deterrent_success).length;
        const cannonCount = deterrents.filter(d => d.cannon_fired).length;
        const speakerCount = deterrents.filter(d => d.speaker_used).length;
        
        setDeterrentStats({
          todayTotal: totalDeterrents,
          todaySuccess: successCount,
          cannonFired: cannonCount,
          speakerUsed: speakerCount,
          successRate: totalDeterrents > 0 ? (successCount / totalDeterrents * 100) : 0,
          avgResponseTime: 3.5 // TODO: 실제 평균 반응시간 계산
        });
        
        // 최근 퇴치 기록 설정
        const recentRecords = deterrents.slice(0, 20).map(deterrent => ({
          detection_id: deterrent.detection_id,
          detection_time: deterrent.deterrent_start_time, // 퇴치 시작 시간 사용
          cannon_fired: deterrent.cannon_fired,
          speaker_used: deterrent.speaker_used,
          is_deterrent_success: deterrent.is_deterrent_success,
          bird_types: deterrent.bird_types || [],
          bb_count: deterrent.bb_count || deterrent.object_count || 0,
          distance: deterrent.distance,
          camera_id: deterrent.camera_id
        }));
        
        setRecentDeterrents(recentRecords);
      } else {
        // 데이터가 없거나 오류인 경우
        setDeterrentStats({
          todayTotal: 0,
          todaySuccess: 0,
          cannonFired: 0,
          speakerUsed: 0,
          successRate: 0,
          avgResponseTime: 0
        });
        setRecentDeterrents([]);
      }
      
    } catch (error) {
      console.error('Error fetching deterrent data:', error);
      // 오류 시 빈 데이터 설정
      setDeterrentStats({
        todayTotal: 0,
        todaySuccess: 0,
        cannonFired: 0,
        speakerUsed: 0,
        successRate: 0,
        avgResponseTime: 0
      });
      setRecentDeterrents([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDeterrentData();
    const interval = setInterval(fetchDeterrentData, 30000); // 30초마다 업데이트
    
    return () => clearInterval(interval);
  }, []);
  
  // 컴포넌트 높이에 따른 표시 개수 계산
  useEffect(() => {
    const calculateVisibleRecords = () => {
      if (!containerRef.current || !recordsContainerRef.current) return;
      
      // 전체 컨테이너 높이
      const containerHeight = containerRef.current.offsetHeight;
      
      // 각 섹션의 실제 높이를 DOM에서 직접 가져오기
      const headerEl = containerRef.current.querySelector('[data-section="header"]');
      const footerEl = containerRef.current.querySelector('[data-section="footer"]');
      const titleEl = containerRef.current.querySelector('[data-section="records-title"]');
      
      const headerHeight = headerEl ? headerEl.offsetHeight : 40;
      const footerHeight = footerEl ? footerEl.offsetHeight : 35;
      const titleHeight = titleEl ? titleEl.offsetHeight : 20;
      
      // 컨테이너 패딩 (p: 1.5 = 12px * 2)
      const containerPadding = 24;
      
      // 사용 가능한 높이 계산 (statsHeight 제거)
      const availableHeight = containerHeight - headerHeight - footerHeight - titleHeight - containerPadding;
      
      // 각 기록 아이템의 실제 높이
      const itemHeight = 32; // 아이템 높이 (minHeight: '32px')
      const itemMargin = 4; // mb: 0.5
      const totalItemHeight = itemHeight + itemMargin;
      
      // 표시 가능한 개수 계산 (마지막 아이템은 마진이 없으므로 +itemMargin)
      const count = Math.floor((availableHeight + itemMargin) / totalItemHeight);
      
      // 최소 5개, 최대 30개
      const finalCount = Math.min(Math.max(count, 5), 30);
      
      setVisibleRecordCount(finalCount);
    };
    
    // DOM이 완전히 로드된 후 계산
    const timer = setTimeout(() => {
      calculateVisibleRecords();
    }, 150);
    
    // 창 크기 변경 시 재계산
    const handleResize = () => {
      calculateVisibleRecords();
    };
    
    window.addEventListener('resize', handleResize);
    
    // ResizeObserver를 사용하여 컨테이너 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleRecords();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [recentDeterrents]); // recentDeterrents 의존성 추가
  
  // 시간 포맷팅
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  // 새로고침
  const handleRefresh = () => {
    fetchDeterrentData();
  };

  return (
    <Paper 
      ref={containerRef}
      elevation={0} 
      sx={{ 
        p: 1.5, 
        borderRadius: 2, 
        backgroundColor: '#050A18',
        border: '1px solid rgba(30, 58, 90, 0.3)',
        height: '100%', // 부모 컨테이너의 전체 높이 사용
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* 헤더 */}
      <Box data-section="header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationIcon sx={{ color: '#FF9800', fontSize: '1.2rem' }} />
          <Typography 
            variant="subtitle2" 
            fontWeight="bold" 
            sx={{ 
              color: 'white',
              textShadow: '0 0 5px rgba(255,255,255,0.5)',
              fontSize: '0.85rem'
            }}
          >
            {translate('조류퇴치 현황', 'Bird Deterrent Status', language)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {deterrentStats.todayTotal > 0 && (
            <Chip 
              label={translate(`${deterrentStats.todayTotal}건`, `Today ${deterrentStats.todayTotal}`, language)}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255, 152, 0, 0.2)',
                color: '#FF9800',
                fontSize: '0.65rem',
                height: '20px'
              }}
            />
          )}
          <Tooltip title={translate('새로고침', 'Refresh', language)}>
            <IconButton size="small" sx={{ color: '#00B4FF', p: 0.5 }} onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* 데이터가 없을 때 안내 메시지 표시 */}
      {deterrentStats.todayTotal === 0 ? (
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <NotificationIcon sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.2)' }} />
          <Typography variant="body2" sx={{ 
            color: 'rgba(255,255,255,0.4)',
            textAlign: 'center'
          }}>
            {translate('퇴치 기록이 없습니다', 'No deterrent records available', language)}
          </Typography>
          <Typography variant="caption" sx={{ 
            color: 'rgba(255,255,255,0.3)',
            textAlign: 'center',
            maxWidth: '80%'
          }}>
            {translate('퇴치 시스템이 작동하면 여기에 기록이 표시됩니다', 'Records will appear here when the deterrent system is activated', language)}
          </Typography>
        </Box>
      ) : (
        <>
          {/* 최근 퇴치 기록 */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0
          }}>
            <Typography 
              data-section="records-title"
              variant="caption" 
              sx={{ 
                color: '#00B4FF', 
                fontSize: '0.7rem', 
                mb: 0.5, 
                display: 'block',
                flexShrink: 0
              }}
            >
              {translate('최근 퇴치 기록', 'Recent Deterrent Records', language)}
            </Typography>
            
            <Box 
              ref={recordsContainerRef}
              sx={{ 
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 180, 255, 0.05)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0, 180, 255, 0.3)',
                  borderRadius: '3px',
                  '&:hover': {
                    background: 'rgba(0, 180, 255, 0.5)',
                  }
                }
              }}
            >
              {recentDeterrents.length > 0 ? (
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  pr: 0.5
                }}>
                  {recentDeterrents.map((record, index) => (
                    <Box key={index} sx={{ 
                      p: 0.8,
                      mb: 0.5,
                      borderRadius: 1,
                      background: record.is_deterrent_success 
                        ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.08) 0%, rgba(76, 175, 80, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(183, 28, 28, 0.08) 0%, rgba(244, 67, 54, 0.05) 100%)',
                      border: '1px solid',
                      borderColor: record.is_deterrent_success 
                        ? 'rgba(76, 175, 80, 0.3)' 
                        : 'rgba(244, 67, 54, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: '32px',
                      flexShrink: 0,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateX(2px)',
                        backgroundColor: record.is_deterrent_success 
                          ? 'rgba(76, 175, 80, 0.12)'
                          : 'rgba(244, 67, 54, 0.12)',
                        borderColor: record.is_deterrent_success 
                          ? 'rgba(76, 175, 80, 0.5)'
                          : 'rgba(244, 67, 54, 0.5)',
                        boxShadow: record.is_deterrent_success
                          ? '0 2px 8px rgba(76, 175, 80, 0.2)'
                          : '0 2px 8px rgba(244, 67, 54, 0.2)',
                        '&::before': {
                          transform: 'translateX(0)',
                        }
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        background: record.is_deterrent_success
                          ? 'linear-gradient(180deg, #4CAF50 0%, #81C784 100%)'
                          : 'linear-gradient(180deg, #F44336 0%, #EF5350 100%)',
                        transform: 'translateX(-3px)',
                        transition: 'transform 0.3s ease'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
                        {/* 시간 표시 - 더 세련된 스타일 */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: '45px'
                        }}>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.9)', 
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.5px'
                          }}>
                            {formatTime(record.detection_time).split(':').slice(0, 2).join(':')}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.4)', 
                            fontSize: '0.5rem',
                            letterSpacing: '0.3px'
                          }}>
                            :{formatTime(record.detection_time).split(':')[2]}
                          </Typography>
                        </Box>
                        
                        {/* 구분선 */}
                        <Box sx={{
                          width: '1px',
                          height: '20px',
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }} />
                        
                        {/* 조류 정보 - 아이콘과 함께 */}
                        <Box sx={{ 
                          flex: 1, 
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          {record.bird_types && record.bird_types.length > 0 ? (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ 
                                color: '#64B5F6', 
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}>
                                {record.bird_types.join(' · ')}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ 
                              color: 'rgba(255,255,255,0.3)', 
                              fontSize: '0.6rem',
                              fontStyle: 'italic'
                            }}>
                              {translate('미확인', 'Unknown', language)}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* 퇴치한 조류 수 표시 */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.4, 
                          flexShrink: 0,
                          backgroundColor: 'rgba(0, 180, 255, 0.1)',
                          borderRadius: '12px',
                          px: 1,
                          py: 0.4,
                          border: '1px solid rgba(0, 180, 255, 0.2)'
                        }}>
                          <Typography sx={{ 
                            color: '#00B4FF',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {record.bb_count || 1}
                          </Typography>
                          <Typography sx={{ 
                            color: 'rgba(0, 180, 255, 0.8)',
                            fontSize: '0.65rem'
                          }}>
                            {translate('마리 퇴치', 'Birds', language)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* 결과 표시 - 더 부드럽게 */}
                      <Box sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <Box sx={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: record.is_deterrent_success 
                            ? 'rgba(76, 175, 80, 0.15)'
                            : 'rgba(255, 152, 0, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: record.is_deterrent_success ? `${pulse} 2s infinite` : 'none'
                        }}>
                          {record.is_deterrent_success ? (
                            <SuccessIcon sx={{ fontSize: '1rem', color: '#66BB6A' }} />
                          ) : (
                            <Box sx={{ fontSize: '0.9rem' }}>🔊</Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <Typography variant="caption" sx={{
                            color: record.is_deterrent_success ? '#66BB6A' : '#FFA726',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            letterSpacing: '0.5px'
                          }}>
                            {translate('발사완료', 'Fired', language)}
                          </Typography>
                          {/* 추가 정보를 작게 표시 */}
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {record.is_deterrent_success && (
                              <Typography variant="caption" sx={{
                                color: 'rgba(76, 175, 80, 0.6)',
                                fontSize: '0.45rem'
                              }}>
                                {translate('성공', 'Success', language)}
                              </Typography>
                            )}
                            {record.response_time && (
                              <Typography variant="caption" sx={{
                                color: 'rgba(255, 255, 255, 0.4)',
                                fontSize: '0.45rem'
                              }}>
                                {record.response_time.toFixed(1)}s
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 3
                }}>
                  <Box sx={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}>
                    <NotificationIcon sx={{ fontSize: '2rem', color: 'rgba(255,255,255,0.2)' }} />
                  </Box>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.7rem',
                    color: 'rgba(255,255,255,0.5)',
                    textAlign: 'center'
                  }}>
                    {translate('아직 퇴치 기록이 없습니다', 'No deterrent records yet', language)}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.6rem',
                    color: 'rgba(255,255,255,0.3)',
                    textAlign: 'center',
                    maxWidth: '80%'
                  }}>
                    {translate('조류가 감지되면 자동으로 퇴치가 시작됩니다', 'Deterrent will start automatically when birds are detected', language)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
      
      {/* 하단 정보 */}
      <Box 
        data-section="footer"
        sx={{ 
          mt: 'auto',
          pt: 1,
          borderTop: '1px solid rgba(0, 180, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Typography variant="caption" sx={{ 
          color: 'rgba(169,234,255,0.6)',
          fontSize: '0.6rem'
        }}>
          {translate('조류퇴치 시스템 v1.0', 'Bird Deterrent System v1.0', language)}
        </Typography>
        <Typography variant="caption" sx={{ 
          color: 'rgba(169,234,255,0.6)',
          fontSize: '0.6rem'
        }}>
          {translate('실시간 모니터링', 'Real-time Monitoring', language)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AIBehaviorAnalysis; 